import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import InterventionQueuePage from "./pages/InterventionQueuePage";
import LandingPageV2 from "./pages/LandingPageV2";
import BrandPrimitivesPage from "./pages/BrandPrimitivesPage";
import GoldenPathPage from "./pages/GoldenPathPage";
import PricingPage from "./pages/PricingPage";
import PipelineAuditPage from "./pages/PipelineAuditPage";
import MissionControlPage from "./pages/MissionControlPage";
import DealRoomPage from "./pages/DealRoomPage";
import DealRoomCanonicalPage from "./pages/DealRoomCanonicalPage";
import DealCommandPage from "./pages/DealCommandPage";
import DealRoomWorkstreamsPage from "./pages/DealRoomWorkstreamsPage";
import SellerLaunchHubPage from "./pages/SellerLaunchHubPage";
import OfferIntelligencePage from "./pages/OfferIntelligencePage";
import MarketplacePage from "./pages/MarketplacePage";
import DocumentsPage from "./pages/DocumentsPage";
// iter-369A · Communication Centre
import CommunicationCentrePage from "./pages/CommunicationCentrePage";
import TodayPage from "./pages/TodayPage";
import AIInsightsPage from "./pages/AIInsightsPage";
import ContactsPage from "./pages/ContactsPage";
import { CommandPalette } from "./components/CommandPalette";
import { PropertyDetailsModal } from "./components/PropertyDetailsModal";
import { AddContactModal } from "./components/contacts/AddContactModal";
import { AddPropertyDrawer } from "./components/properties/AddPropertyDrawer";
import { CreateDealModal } from "./components/deals/CreateDealModal";
import { EditDealModal } from "./components/deals/EditDealModal";
import { NotificationCenter } from "./components/global/NotificationCenter";
import { GlobalUndoToast } from "./components/operational/GlobalUndoToast";
import { MobileTodayLauncher } from "./components/operational/MobileTodayLauncher";
import { FloatingTopBar } from "./components/global/FloatingTopBar";
import { GlobalCommandBar, GlobalCommandBarMobile } from "./components/global/GlobalCommandBar";
import { TopCommandRail } from "./components/global/TopCommandRail";
import { MobileTopBar } from "./components/global/MobileTopBar";
import { InstallPrompt } from "./components/global/InstallPrompt";
import { NotificationPrompt } from "./components/global/NotificationPrompt";
import { ShellContext } from "./components/global/ShellContext";
import { Sidebar } from "./components/Sidebar";
import SellerPortalPage from "./pages/SellerPortalPage";
import BuyerPortalPage from "./pages/BuyerPortalPage";
import SolicitorPortalPage from "./pages/SolicitorPortalPage";
import ContactProfilePage from "./pages/ContactProfilePage";
import PropertyProfilePage from "./pages/PropertyProfilePage";
import PropertiesPage from "./pages/PropertiesPage";
import DealsPage from "./pages/DealsPage";
import SalesProgressionForecastPage from "./pages/SalesProgressionForecastPage";
import ForecastingCentrePage from "./pages/ForecastingCentrePage";
import DealRoomsIndexPage from "./pages/DealRoomsIndexPage";
import { MobileBottomNav, GlobalMobileBottomSpacer } from "./components/MobileBottomNav";
import { ChainStoreProvider } from "./data/chainStore";
import { AuthProvider, RequireAuth } from "./data/authStore";
import LoginPage from "./pages/auth/LoginPage";
import SignupPage from "./pages/auth/SignupPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import AcceptInvitePage from "./pages/auth/AcceptInvitePage";
import JoinDealPage from "./pages/auth/JoinDealPage";
import MagicLinkRequestPage from "./pages/auth/MagicLinkRequestPage";
import MagicLinkConsumePage from "./pages/auth/MagicLinkConsumePage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import TeamPage from "./pages/TeamPage";
import PeoplePage from "./pages/PeoplePage";
import NotificationsPage from "./pages/NotificationsPage";
import SettingsPage from "./pages/SettingsPage";
import EmailAccountsPage from "./pages/settings/EmailAccountsPage";
import DemoControlPage from "./pages/DemoControlPage";
import ClientExperiencePage from "./pages/ClientExperiencePage";
import PartnerEcosystemPage from "./pages/PartnerEcosystemPage";
import { PrototypeGate } from "./pages/PrototypeGate";
import { Toaster } from "@/components/ui/sonner";
import WalkthroughCompanion from "./components/director/WalkthroughCompanion";
import { SampleDataProvider } from "./hooks/useSampleData";
import { SampleDataBanner } from "./components/sample-data/SampleDataBanner";
// Side-effect import: registers the calm `cf-pulse` keyframe used
// across all loading skeletons platform-wide.
import "./components/ui/operational-skeleton";

// Chain Chat is now embedded inside Deal Room — preserve deep-linking
// by routing /chain-chat/:dealId to the Deal Room conversation tab.
function ChainChatRedirect() {
  const { dealId } = useParams();
  return <Navigate to={`/deal-rooms/${dealId}/room`} replace />;
}

// iter-186 — the Canonical Deal Room (pre-pilot direction) is now
// the DEFAULT surface at `/deal-rooms/:dealId`. Fallback routes:
//   • `/deal-rooms/:dealId/workstreams` → operational workstreams table
//   • `/deal-rooms/:dealId/room`        → legacy tab-based Deal Room
//   • `/deal-rooms/:dealId/legacy`      → V3 (one-release safety hatch)
//   • `?workstreams=1` query also opts INTO the Workstreams surface
//     from the canonical route for fallback debugging.
function DealRoomRouter() {
  const location = useLocation();
  const useWorkstreams = (() => {
    try {
      const url = new URLSearchParams(location.search);
      return url.get("workstreams") === "1";
    } catch { return false; }
  })();
  return useWorkstreams ? <DealRoomWorkstreamsPage /> : <DealRoomCanonicalPage />;
}

/* iter-alignment · Sidebar Reorder + Command Centre Landing (2026-02-28)
 * -----------------------------------------------------------------
 * SessionDayGuard — enforces the "new day / new session ⇒ Command
 * Centre" rule while preserving the operator's last route during
 * the same working session.  Storage-only heuristic (no fetch, no
 * new endpoint):
 *
 *   • `chainflow:last-visit-day` (localStorage) holds the calendar
 *     day (YYYY-MM-DD in the operator's timezone) of the last
 *     recorded navigation.
 *   • On mount + on route change, if the stored day differs from
 *     TODAY (or is missing) the guard redirects to `/today` and
 *     refreshes the stamp — the operator lands on Command Centre
 *     at the start of every new day.
 *   • If the operator is already ON `/today` OR on a public/auth
 *     route, the guard just refreshes the stamp — no redirect.
 *   • Same-day navigation refreshes the stamp only; the operator's
 *     browser history / most-recent page is preserved verbatim.
 *
 * Public (non-authed) routes are excluded so anonymous visitors
 * hitting the landing / marketing / login pages are never bounced. */
const _PUBLIC_PATH_PREFIXES = [
  "/", "/login", "/signup", "/forgot-password", "/reset-password",
  "/accept-invite", "/join-deal", "/access", "/verify-email",
  "/solicitor-portal", "/pricing", "/golden-path", "/legacy-landing",
  "/dev/",
];
const _isPublicPath = (path) => {
  if (path === "/") return true;
  return _PUBLIC_PATH_PREFIXES.some((p) => p !== "/" && (path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`)));
};
const _todayStamp = () => {
  try {
    const d = new Date();
    const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch { return ""; }
};
function SessionDayGuard() {
  const location = useLocation();
  const nav = useNavigate();
  const didRedirectRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = location.pathname || "/";
    if (_isPublicPath(path)) return; // don't gate anonymous surfaces
    let stored = "";
    try { stored = window.localStorage.getItem("chainflow:last-visit-day") || ""; } catch { /* noop */ }
    const today = _todayStamp();
    // New day OR first-ever visit → redirect to Command Centre once
    // per session, then let the operator navigate freely for the
    // rest of the day.
    if (stored !== today && !didRedirectRef.current && path !== "/today") {
      didRedirectRef.current = true;
      try { window.localStorage.setItem("chainflow:last-visit-day", today); } catch { /* noop */ }
      nav("/today", { replace: true });
      return;
    }
    // Same-day navigation → just refresh the stamp so a browser
    // reload later the same day keeps them on their current route.
    if (stored !== today) {
      try { window.localStorage.setItem("chainflow:last-visit-day", today); } catch { /* noop */ }
    }
  }, [location.pathname, nav]);
  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <div
      key={location.pathname}
      className="cf-page-enter xl:pt-[var(--cf-topbar-h,0px)]"
      data-testid="cf-page-enter"
    >
      <SessionDayGuard />
      <Routes location={location}>
        <Route path="/" element={<LandingPageV2 />} />
        <Route path="/dev/brand-primitives" element={<BrandPrimitivesPage />} />
        <Route path="/golden-path" element={<GoldenPathPage />} />
        <Route path="/pipeline-audit" element={<PipelineAuditPage />} />
        <Route path="/legacy-landing" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/join-deal" element={<JoinDealPage />} />
        <Route path="/access" element={<MagicLinkRequestPage />} />
        <Route path="/access/return" element={<MagicLinkConsumePage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        {/* Solicitor Magic-Link Portal — public, token-scoped, no auth */}
        <Route path="/solicitor-portal/access" element={<SolicitorPortalPage />} />
        <Route path="/solicitor-portal" element={<SolicitorPortalPage />} />

        {/* Protected routes */}
        <Route path="/flow-centre"                element={<Navigate to="/today" replace />} />
        <Route path="/dashboard"                 element={<RequireAuth><TodayPage /></RequireAuth>} />
        <Route path="/dashboard-legacy"          element={<Navigate to="/dashboard" replace />} />
        <Route path="/command"                   element={<Navigate to="/mission-control" replace />} />
        <Route path="/deal-rooms"                element={<RequireAuth><DealRoomsIndexPage /></RequireAuth>} />
        <Route path="/deal-rooms/:dealId"        element={<RequireAuth><DealRoomRouter /></RequireAuth>} />
        {/* iter-186 — explicit fallback routes (canonical promoted to default). */}
        <Route path="/deal-rooms/:dealId/workstreams" element={<RequireAuth><DealRoomWorkstreamsPage /></RequireAuth>} />
        <Route path="/deal-rooms/:dealId/canonical"   element={<Navigate to="../" relative="path" replace />} />
        <Route path="/deal-rooms/:dealId/legacy"  element={<RequireAuth><DealCommandPage /></RequireAuth>} />
        <Route path="/deal-rooms/:dealId/room"    element={<RequireAuth><DealRoomPage /></RequireAuth>} />
        <Route path="/deal-command/:dealId"      element={<RequireAuth><DealCommandPage /></RequireAuth>} />
        {/* iter-225B — Pilot dual-surface clarification:
            • /pipeline           → SalesProgressionForecastPage
              (forecasting · exchange visibility · revenue · director
              overview)
            • /sales-progression  → SalesProgressionPage (operational
              deal-by-deal progression desk — stage-grouped queues)
            • /sales-progression/legacy → archived V2 UI for
              reference
            Both surfaces are intentional and have distinct purposes;
            users see them as separate sidebar entries. */}
        <Route path="/pipeline"                  element={<RequireAuth><SalesProgressionForecastPage /></RequireAuth>} />
        <Route path="/sales-progression"         element={<RequireAuth><ForecastingCentrePage /></RequireAuth>} />
        <Route path="/forecasting-centre"         element={<RequireAuth><ForecastingCentrePage /></RequireAuth>} />
        {/* Sales Progression hidden from users; pages preserved, addresses redirect to Pipeline. */}
        <Route path="/sales-progression/legacy"  element={<Navigate to="/pipeline" replace />} />
        <Route path="/sales-progression/queues"  element={<Navigate to="/pipeline" replace />} />
        <Route path="/seller-launch-hub"         element={<RequireAuth><PrototypeGate matchPath="/seller-launch-hub"><SellerLaunchHubPage /></PrototypeGate></RequireAuth>} />
        <Route path="/seller-launch-hub/:propertyId" element={<RequireAuth><PrototypeGate matchPath="/seller-launch-hub"><SellerLaunchHubPage /></PrototypeGate></RequireAuth>} />
        <Route path="/offer-intelligence"        element={<RequireAuth><PrototypeGate matchPath="/offer-intelligence"><OfferIntelligencePage /></PrototypeGate></RequireAuth>} />
        <Route path="/offer-intelligence/:propertyId" element={<RequireAuth><PrototypeGate matchPath="/offer-intelligence"><OfferIntelligencePage /></PrototypeGate></RequireAuth>} />
        <Route path="/marketplace"               element={<RequireAuth><PrototypeGate matchPath="/marketplace"><MarketplacePage /></PrototypeGate></RequireAuth>} />
        <Route path="/documents"                 element={<RequireAuth><DocumentsPage /></RequireAuth>} />
        <Route path="/documents/:dealId"         element={<RequireAuth><DocumentsPage /></RequireAuth>} />
        <Route path="/communication"             element={<RequireAuth><CommunicationCentrePage /></RequireAuth>} />
        <Route path="/today"                     element={<RequireAuth><TodayPage /></RequireAuth>} />
        <Route path="/ai-insights"               element={<Navigate to="/insights" replace />} />
        <Route path="/contacts"                  element={<RequireAuth><ContactsPage /></RequireAuth>} />
        <Route path="/contacts/:contactId"       element={<RequireAuth><ContactProfilePage /></RequireAuth>} />
        <Route path="/properties"                element={<RequireAuth><PropertiesPage /></RequireAuth>} />
        <Route path="/properties/:propertyId"    element={<RequireAuth><PropertyProfilePage /></RequireAuth>} />
        <Route path="/deals"                     element={<RequireAuth><DealsPage /></RequireAuth>} />
        <Route path="/seller-portal"             element={<RequireAuth><PrototypeGate matchPath="/seller-portal"><SellerPortalPage /></PrototypeGate></RequireAuth>} />
        <Route path="/seller-portal/:dealId"     element={<RequireAuth><PrototypeGate matchPath="/seller-portal"><SellerPortalPage /></PrototypeGate></RequireAuth>} />
        <Route path="/buyer-portal"              element={<RequireAuth><PrototypeGate matchPath="/buyer-portal"><BuyerPortalPage /></PrototypeGate></RequireAuth>} />
        <Route path="/buyer-portal/:dealId"      element={<RequireAuth><PrototypeGate matchPath="/buyer-portal"><BuyerPortalPage /></PrototypeGate></RequireAuth>} />
        <Route path="/teams"                     element={<RequireAuth><TeamPage /></RequireAuth>} />
        <Route path="/team"                      element={<RequireAuth><TeamPage /></RequireAuth>} />
        <Route path="/people"                    element={<Navigate to="/contacts" replace />} />
        <Route path="/branch-analytics"          element={<Navigate to="/insights" replace />} />
        <Route path="/insights"                  element={<RequireAuth><AIInsightsPage /></RequireAuth>} />
        <Route path="/mission-control"            element={<RequireAuth><MissionControlPage /></RequireAuth>} />
        <Route path="/interventions"              element={<RequireAuth><InterventionQueuePage /></RequireAuth>} />
        {/* IA refactor 2026-02-15 — every former standalone surface
            collapses into one of the 9 primary destinations. Mission
            Control is the operational heartbeat; Deals embeds the
            transaction-adjacent surfaces (properties / viewings /
            sales-progression); Settings absorbs the infrastructure
            layer (automations / integrations / organisation). */}
        <Route path="/director-centre"            element={<Navigate to="/mission-control?view=operations" replace />} />
        <Route path="/director"                   element={<Navigate to="/mission-control?view=operations" replace />} />
        <Route path="/director-operations"        element={<Navigate to="/mission-control?view=operations" replace />} />
        <Route path="/revenue"                    element={<Navigate to="/mission-control?view=operations" replace />} />
        <Route path="/revenue-command"            element={<Navigate to="/mission-control?view=operations" replace />} />
        <Route path="/office-performance"         element={<Navigate to="/mission-control?view=operations" replace />} />
        <Route path="/director-legacy"            element={<Navigate to="/mission-control" replace />} />
        <Route path="/viewings"                   element={<Navigate to="/deals?tab=viewings" replace />} />
        <Route path="/chain-chat"                 element={<Navigate to="/deals" replace />} />
        <Route path="/chain-chat/:dealId"         element={<ChainChatRedirect />} />
        <Route path="/automations"                element={<Navigate to="/settings?tab=automations" replace />} />
        <Route path="/integrations"               element={<Navigate to="/settings?tab=integrations" replace />} />
        <Route path="/organisation"               element={<Navigate to="/settings?tab=organisation" replace />} />
        <Route path="/notifications"             element={<RequireAuth><NotificationsPage /></RequireAuth>} />
        <Route path="/alerts"                    element={<Navigate to="/notifications" replace />} />
        {/* Operational IA — new sidebar destinations
            Chains  → Mission Control with chain operations view
            Reports → Mission Control operations view
            Tasks   → Today (mobile-first daily plan, includes tasks block) */}
        <Route path="/chains"                    element={<Navigate to="/mission-control?view=chains" replace />} />
        <Route path="/reports"                   element={<Navigate to="/mission-control?view=operations" replace />} />
        <Route path="/tasks"                     element={<Navigate to="/today?focus=tasks" replace />} />
        <Route path="/settings"                  element={<RequireAuth><SettingsPage /></RequireAuth>} />
        <Route path="/settings/email"             element={<RequireAuth><EmailAccountsPage /></RequireAuth>} />
        <Route path="/settings/demo-control"      element={<RequireAuth><DemoControlPage /></RequireAuth>} />
        {/* Client Experience Layer — calm, premium buyer/seller surface */}
        <Route path="/client-view"                 element={<RequireAuth><ClientExperiencePage /></RequireAuth>} />
        <Route path="/client-view/:dealId"         element={<RequireAuth><ClientExperiencePage /></RequireAuth>} />
        <Route path="/client-view/by-token/:token" element={<ClientExperiencePage tokenScoped />} />
        {/* Partner Ecosystem Layer — multi-party operational coordination */}
        <Route path="/partners"                    element={<RequireAuth><PartnerEcosystemPage /></RequireAuth>} />
        {/* Sample-data calm IA redirects — /sellers and /buyers
            collapse into Contacts with a pre-applied role filter. */}
        <Route path="/sellers"                     element={<Navigate to="/contacts?filter=sellers" replace />} />
        <Route path="/buyers"                      element={<Navigate to="/contacts?filter=buyers" replace />} />
        <Route path="*" element={<Navigate to="/today" replace />} />
      </Routes>
    </div>
  );
}

/* ── Routes that should NOT render the persistent sidebar shell ────
   Public/unauthenticated surfaces — landing, auth flows, etc. */
const UNAUTH_PATHS = new Set([
  "/",
  "/golden-path",
  "/legacy-landing",
  "/pricing",
  "/pipeline-audit",
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/accept-invite",
  "/join-deal",
  "/access",
  "/access/return",
  "/verify-email",
]);

/* ── PersistentShell — the single Sidebar instance for the entire app.
   Mounts ONCE per session, survives every route change because it lives
   OUTSIDE the keyed <AnimatedRoutes> wrapper. Page-level <Sidebar />
   renders detect ShellContext=true and skip, so we ship without
   touching the 24 legacy pages that render their own Sidebar. */
function PersistentShell() {
  const location = useLocation();
  if (UNAUTH_PATHS.has(location.pathname)) return null;
  // Client Experience Layer is buyer/seller-facing — never render the operator shell.
  if (location.pathname.startsWith("/client-view")) return null;
  // Solicitor portal is public + token-scoped — never render the operator shell.
  if (location.pathname.startsWith("/solicitor-portal")) return null;
  return <Sidebar __isShellOwner />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ChainStoreProvider>
            <SampleDataProvider>
              <ShellContext.Provider value={true}>
                <MobileTopBar />
                <PersistentShell />
                <TopCommandRail />
                <SampleDataBanner />
                <AnimatedRoutes />
                <GlobalMobileBottomSpacer />
                <MobileBottomNav />
                <CommandPalette />
                <PropertyDetailsModal />
                <AddContactModal />
                <AddPropertyDrawer />
                <CreateDealModal />
                <EditDealModal />
                <NotificationCenter />
                {/* iter-201D.A — FloatingTopBar (bell) + GlobalCommandBar
                    (fixed pill) are replaced by <TopCommandRail />,
                    which hosts both inside a single calm top rail
                    that reserves vertical space (no more overlap with
                    page headings). Mobile keeps its floating button. */}
                <GlobalCommandBarMobile />
                <InstallPrompt />
                <NotificationPrompt />
                <WalkthroughCompanion />
                <GlobalUndoToast />
                <MobileTodayLauncher />
                <Toaster position="bottom-right" />
              </ShellContext.Provider>
            </SampleDataProvider>
          </ChainStoreProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
